import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { EASE_CALM } from "../../lib/motion";
import { type TWipeRevealRole, resolveWipeRevealTarget } from "./wipe-reveal-targets";

const WIPE_MASK =
	"linear-gradient(90deg, var(--color-black) 0%, var(--color-black) 34%, color-mix(in srgb, var(--color-black) 88%, transparent) 46%, color-mix(in srgb, var(--color-black) 50%, transparent) 56%, color-mix(in srgb, var(--color-black) 18%, transparent) 66%, transparent 78%)";

type TWipeRevealTextProps = {
	role: TWipeRevealRole;
	children: ReactNode;
	className?: string;
	as?: "h1" | "h2" | "p";
	wipeInDuration?: number;
	wipeInDelay?: number;
	wipeOutDuration?: number;
};

export function WipeRevealText({
	role,
	children,
	className,
	as: Tag = "h2",
	wipeInDuration = 2.4,
	wipeInDelay = 0.16,
	wipeOutDuration = 1.1,
}: TWipeRevealTextProps) {
	const reducedMotion = useReducedMotion();
	const MotionTag = motion[Tag];
	const masked = role === "wipe-in" || role === "wipe-out";
	const visible = role === "shown" || role === "wipe-in";

	if (reducedMotion) {
		return (
			<Tag className={className} style={{ opacity: visible ? 1 : 0 }} aria-hidden={!visible}>
				{children}
			</Tag>
		);
	}

	return (
		<MotionTag
			className={className}
			initial={false}
			animate={resolveWipeRevealTarget(role)}
			aria-hidden={!visible}
			transition={
				role === "wipe-in"
					? { duration: wipeInDuration, delay: wipeInDelay, ease: "linear" }
					: role === "wipe-out"
						? { duration: wipeOutDuration, ease: EASE_CALM }
						: { duration: 0 }
			}
			style={
				masked
					? {
							WebkitMaskImage: WIPE_MASK,
							maskImage: WIPE_MASK,
							WebkitMaskSize: "340% 100%",
							maskSize: "340% 100%",
							WebkitMaskRepeat: "no-repeat",
							maskRepeat: "no-repeat",
						}
					: {
							WebkitMaskImage: "none",
							maskImage: "none",
						}
			}
		>
			{children}
		</MotionTag>
	);
}

export function wipeRevealMessageClassName(choicesLayout?: boolean) {
	return cn(
		"m-0 w-full text-center font-sans text-[clamp(28px,calc(24px+0.74vw),34px)] leading-[1.25] tracking-[-0.03em] font-medium text-teal-500",
		{
			"max-lg:max-w-[264px] max-lg:justify-self-center": choicesLayout,
		},
	);
}
